"use client";

import { Badge } from "@/components/ui/badge";
import { BadgeQuestionMarkIcon, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TransitionPanel } from "@/components/ui/transition-panel";
import { useMemo, useState } from "react";
import { siteConfig } from "@/lib/siteConfig";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { GridSvg } from "@/components/gridSvg";

export default function QuestionsComponent() {
  const [activeIndex, setActiveIndex] = useState<number>(
    siteConfig.faqCategories.length,
  );
  const [searchQuestion, setSearchQuestion] = useState("");

  const allQuestions = useMemo(
    () => siteConfig.faqCategories.flatMap((category) => category.questions),
    [],
  );

  return (
    <>
      <section
        className={"wrapper hero py-10 "}
        style={{
          maskImage:
            "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)", // Added mask for fading edges
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)", // Added for Safari compatibility
        }}
      >
        <div className={"flex flex-col items-center text-center gap-y-10"}>
          <Badge>
            <BadgeQuestionMarkIcon /> <p>Frequently Asked Questions</p>
          </Badge>
          <h1
            className={
              "text-3xl lg:text-5xl font-bold text-5xl font-bold bg-gradient-to-r from-primary via-primary/90 to-emerald-400 bg-clip-text text-transparent"
            }
          >
            Got <br />
            Questions?
          </h1>
          <p className={"text-lg text-muted-foreground lg:w-1/2 font-semibold"}>
            Find answers to common questions about our services, products, and
            operations. Can&apos;t find what you&apos;re looking for? We&apos;re
            here to help.
          </p>

          <Input
            placeholder={"Search questions..."}
            className={"md:max-w-[50%] !bg-background"}
            onChange={(e) => setSearchQuestion(e.target.value)}
          />
        </div>
      </section>
      <section className={"wrapper relative py-10"}>
        <GridSvg />
        <div
          className={
            "flex flex-wrap-reverse flex-row-reverse gap-5 justify-center"
          }
        >
          {siteConfig.faqCategories.map((item, index) => (
            <Button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`rounded-md  hover:text-white px-3 py-1 text-sm font-medium ${
                activeIndex === index
                  ? "bg-primary"
                  : "bg-zinc-100 text-muted-foreground dark:bg-zinc-700 "
              }`}
            >
              {item.title} (
              {
                siteConfig.faqCategories[index].questions.filter(
                  (predicate) =>
                    predicate.question
                      .toLowerCase()
                      .includes(searchQuestion.toLowerCase()) ||
                    predicate.answer
                      .toLowerCase()
                      .includes(searchQuestion.toLowerCase()),
                ).length
              }
              )
            </Button>
          ))}
          <Button
            onClick={() => setActiveIndex(siteConfig.faqCategories.length)}
            className={`rounded-md  hover:text-white px-3 py-1 text-sm font-medium ${
              activeIndex === siteConfig.faqCategories.length
                ? "bg-primary"
                : "bg-zinc-100 text-muted-foreground dark:bg-zinc-700 "
            }`}
          >
            All Questions (
            {
              allQuestions.filter(
                (predicate) =>
                  predicate.question
                    .toLowerCase()
                    .includes(searchQuestion.toLowerCase()) ||
                  predicate.answer
                    .toLowerCase()
                    .includes(searchQuestion.toLowerCase()),
              ).length
            }
            )
          </Button>
        </div>
        <div className={"py-14 grid gap-5"}>
          {activeIndex === siteConfig.faqCategories.length ? (
            allQuestions
              .filter(
                (predicate) =>
                  predicate.question
                    .toLowerCase()
                    .includes(searchQuestion.toLowerCase()) ||
                  predicate.answer
                    .toLowerCase()
                    .includes(searchQuestion.toLowerCase()),
              )
              .map((question, questionIndex) => (
                <Accordion
                  key={questionIndex}
                  className="flex w-full flex-col"
                  transition={{
                    type: "spring",
                    stiffness: 120,
                    damping: 20,
                  }}
                  variants={{
                    expanded: {
                      opacity: 1,
                      scale: 1,
                    },
                    collapsed: {
                      opacity: 0,
                      scale: 0.7,
                    },
                  }}
                >
                  <AccordionItem
                    value={question.question}
                    className="square-gradient-bg   "
                  >
                    <AccordionTrigger className="w-full p-5 cursor-pointer text-left ">
                      <div className="flex items-center text-primary">
                        <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-expanded:rotate-90 " />
                        <div className="ml-2 text-primary font-bold">
                          {question.question}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="origin-left">
                      <p className="pl-6 pb-5 pr-2 text-foreground text-sm">
                        {question.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))
          ) : (
            <TransitionPanel
              activeIndex={activeIndex as number}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              variants={{
                enter: { opacity: 0, y: -50, filter: "blur(4px)" },
                center: { opacity: 1, y: 0, filter: "blur(0px)" },
                exit: { opacity: 0, y: 50, filter: "blur(4px)" },
              }}
            >
              {siteConfig.faqCategories.map((item, index) => (
                <div key={index} className="grid gap-5">
                  {item.questions
                    .filter(
                      (predicate) =>
                        predicate.question
                          .toLowerCase()
                          .includes(searchQuestion.toLowerCase()) ||
                        predicate.answer
                          .toLowerCase()
                          .includes(searchQuestion.toLowerCase()),
                    )
                    .map((question, questionIndex) => (
                      <Accordion
                        key={questionIndex}
                        className="flex w-full flex-col"
                        transition={{
                          type: "spring",
                          stiffness: 120,
                          damping: 20,
                        }}
                        variants={{
                          expanded: {
                            opacity: 1,
                            scale: 1,
                          },
                          collapsed: {
                            opacity: 0,
                            scale: 0.7,
                          },
                        }}
                      >
                        <AccordionItem
                          value={question.question}
                          className="square-gradient-bg   "
                        >
                          <AccordionTrigger className="w-full p-5 cursor-pointer text-left ">
                            <div className="flex items-center text-primary">
                              <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-expanded:rotate-90 " />
                              <div className="ml-2 text-primary font-bold">
                                {question.question}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="origin-left">
                            <p className="pl-6 pb-5 pr-2 text-foreground text-sm">
                              {question.answer}
                            </p>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ))}
                </div>
              ))}
            </TransitionPanel>
          )}
        </div>
      </section>
      <section id={"contact"} className={"wrapper  py-10"}>
        <div className={"flex flex-col items-center gap-8 text-center"}>
          <Badge>
            <BadgeQuestionMarkIcon />
            Still Have Questions?
          </Badge>
          <h1 className={"text-3xl lg:text-4xl font-bold"}>
            We&apos;re Here to <span className={"text-primary"}>Help You</span>
          </h1>
          <p className={"text-lg text-muted-foreground font-semibold lg:w-1/2"}>
            Can&apos;t find the answer you&apos;re looking for? Our team is
            ready to assist you with any questions about our products and
            services.
          </p>
          <div className={"grid grid-cols-1 md:grid-cols-3 gap-5"}>
            {siteConfig.contactMethods.map((contact, index) => (
              <div
                key={index}
                className={
                  "flex flex-col gap-2 items-center text-center border rounded p-10"
                }
              >
                <span className={"text-primary bg-primary/10 p-5 mb-3 rounded"}>
                  <contact.icon />
                </span>
                <h1 className={"text-xl font-bold"}>{contact.title}</h1>
                <p className={"text-muted-foreground text-sm font-semibold"}>
                  {contact.description}
                </p>
                <p className={"text-primary font-semibold mt-5"}>
                  {contact.contact}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
